import {IconUsers} from "@tabler/icons-react";
import {memo, useCallback, useRef, useState} from "react";
import {openGamanzaPlayerDrawer} from "@/components/GamanzaPlayerDrawer/gamanzaPlayerDrawerEvents";
import useGamanzaWidgetHydration from "@/hooks/useGamanzaWidgetHydration";
import {useAuthStore} from "@/store/useAuth.store";
import {getUserInitials} from "@/util/avatarUtils";
import {useIsGamanzaUiEnabled} from "@/utils/gamanzaUi";
import styles from "./StoryItem.module.css";

/**
 * @param {Object} props
 * @param {import("../stories.constants").Story} props.story
 * @param {(story: import("../stories.constants").Story, anchorElement?: HTMLElement) => void} props.onOpen
 */
const StoryItem = ({story, onOpen}) => {
  const [imgError, setImgError] = useState(false);
  const itemRef = useRef(null);
  const gamanzaHostRef = useRef(null);
  const gamanzaUiEnabled = useIsGamanzaUiEnabled();
  const authStatus = useAuthStore(state => state.authStatus);
  const token = useAuthStore(state => state.token);
  const user = useAuthStore(state => state.user);

  const isAuthenticated = authStatus === "authenticated" && Boolean(token);
  const isUserStory = story.type === "user" && isAuthenticated;
  const useGamanzaWidget = isUserStory && gamanzaUiEnabled;
  const {isHydrating} = useGamanzaWidgetHydration({
    enabled: useGamanzaWidget,
    token,
    rootRef: gamanzaHostRef,
  });
  const showSkeleton = useGamanzaWidget && isHydrating;
  const userInitials = getUserInitials(user);

  const handleClick = useCallback(() => {
    if (useGamanzaWidget) {
      openGamanzaPlayerDrawer();
      return;
    }

    const scrollY = window.scrollY;
    const row = itemRef.current?.closest("[class*='stories__row']");
    const scrollLeft = row?.scrollLeft ?? 0;

    onOpen(story, itemRef.current);

    requestAnimationFrame(() => {
      window.scrollTo({top: scrollY, behavior: "instant"});
      if (row) row.scrollLeft = scrollLeft;
    });
  }, [onOpen, story, useGamanzaWidget]);

  const handleKeyDown = useCallback(
    e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        if (useGamanzaWidget) {
          openGamanzaPlayerDrawer();
          return;
        }
        onOpen(story, itemRef.current);
      }
    },
    [onOpen, story, useGamanzaWidget]
  );

  const hasLevelProgress =
    story.type === "user" &&
    story.levelProgress != null &&
    Number.isFinite(Number(story.levelProgress));

  const levelProgress = hasLevelProgress
    ? Math.max(0, Math.min(100, Number(story.levelProgress)))
    : null;

  const ringClass = [
    styles["story-item__ring"],
    hasLevelProgress
      ? styles["story-item__ring--profileProgress"]
      : story.ringVariant
        ? styles[`story-item__ring--${story.ringVariant}`]
        : "",
    story.viewed && !hasLevelProgress ? styles["story-item__ring--viewed"] : "",
    story.viewed && hasLevelProgress ? styles["story-item__ring--viewedProgress"] : "",
  ]
    .filter(Boolean)
    .join(" ");

  const ringStyle = hasLevelProgress ? {"--level-progress": levelProgress} : undefined;

  const renderAvatar = () => {
    if (story.type === "community") {
      return (
        <span className={styles["story-item__community-icon"]}>
          <IconUsers size={26} />
        </span>
      );
    }

    if (story.type === "user") {
      if (!imgError && story.icon) {
        return (
          <img
            className={styles["story-item__avatar-img"]}
            src={story.icon}
            alt={story.label}
            onError={() => setImgError(true)}
          />
        );
      }
      return <span className={styles["story-item__initials"]}>{userInitials}</span>;
    }

    if (story.type === "provider" && story.icon) {
      return (
        <img
          className={styles["story-item__avatar-img"]}
          src={story.icon}
          alt={story.label}
          loading="lazy"
        />
      );
    }

    if (story.icon) {
      return (
        <img
          className={styles["story-item__avatar-img"]}
          src={story.icon}
          alt={story.label}
          loading="lazy"
          onError={() => setImgError(true)}
        />
      );
    }

    return null;
  };

  return (
    <div
      ref={itemRef}
      className={styles["story-item"]}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={
        useGamanzaWidget
          ? `Abrir gamificación: ${story.label}`
          : `Ver historia: ${story.label}`
      }
    >
      {useGamanzaWidget ? (
        <div className={styles["story-item__ringWrap"]}>
          <div
            ref={gamanzaHostRef}
            className={styles["story-item__gamanzaHost"]}
            aria-busy={showSkeleton}
            aria-hidden
          >
            {showSkeleton && <span className={styles["story-item__skeleton"]} />}
            <div
              className={`gamification_widget ${styles["story-item__gamanzaWidget"]} ${
                showSkeleton ? styles["story-item__widgetPending"] : styles["story-item__widgetReady"]
              }`}
              data-type="avatar"
              data-size="50"
              data-show-level="true"
              data-progress-type="level"
            />
          </div>
        </div>
      ) : (
        <div className={styles["story-item__ringWrap"]}>
          <div
            className={ringClass}
            style={ringStyle}
          >
            <div className={styles["story-item__avatar"]}>{renderAvatar()}</div>
          </div>
        </div>
      )}
      <span className={styles["story-item__label"]}>{story.label}</span>
    </div>
  );
};

export default memo(StoryItem);
